import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Building2, Calendar, Filter, X, Bookmark, BookmarkCheck, ChevronLeft, ChevronRight, RefreshCw, Loader2, Clock, Plus, ChevronDown, ChevronUp, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";

export default function JobsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ 
    fields: [], 
    countries: [], 
    institutions: [], 
    sources: [],
    source_counts: {}
  });
  const [bookmarkedJobs, setBookmarkedJobs] = useState(new Set());
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Multi-select filter states (arrays instead of single values)
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState([]);
  const [sortBy, setSortBy] = useState("posted_date");
  
  // Expanded filter sections
  const [expandedSections, setExpandedSections] = useState({
    jobType: true,
    source: true,
    field: false,
    country: false
  });

  const JOB_TYPES = [
    { value: "postdoc", label: "Postdoctoral" },
    { value: "job", label: "Research Jobs" },
    { value: "fellowship", label: "Fellowships" }
  ];

  // Fetch filter options and scrape status
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [filtersRes, statusRes] = await Promise.all([
          fetch(`${API}/jobs/filters/options`),
          fetch(`${API}/jobs/scrape/status`)
        ]);
        
        if (filtersRes.ok) {
          const data = await filtersRes.json();
          setFilterOptions(data);
        }
        
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setLastRefresh(statusData.last_refresh);
        }
      } catch (error) {
        console.error("Error fetching filters:", error);
      }
    };
    fetchFilters();
  }, []);

  // Fetch bookmarks if user is logged in
  useEffect(() => {
    const fetchBookmarks = async () => {
      if (!user) return;
      try {
        const response = await fetch(`${API}/bookmarks`, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          setBookmarkedJobs(new Set(data.map(b => b.job_id)));
        }
      } catch (error) {
        console.error("Error fetching bookmarks:", error);
      }
    };
    fetchBookmarks();
  }, [user]);

  // Fetch jobs with multi-select filters
  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.set("search", searchQuery);
        if (selectedFields.length > 0) params.set("field", selectedFields.join(","));
        if (selectedCountries.length > 0) params.set("country", selectedCountries.join(","));
        if (selectedSources.length > 0) params.set("source", selectedSources.join(","));
        if (selectedJobTypes.length > 0) params.set("job_type", selectedJobTypes.join(","));
        params.set("sort_by", sortBy);
        params.set("page", pagination.page.toString());
        params.set("limit", "12");

        const response = await fetch(`${API}/jobs?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          setJobs(data.jobs || []);
          setPagination(prev => ({
            ...prev,
            total: data.total,
            pages: data.pages
          }));
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
        toast.error("Failed to load jobs");
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [searchQuery, selectedFields, selectedCountries, selectedSources, selectedJobTypes, sortBy, pagination.page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Toggle functions for multi-select
  const toggleFilter = (value, selectedArray, setSelectedArray) => {
    setSelectedArray(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Refresh jobs from sources
  const handleRefreshJobs = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API}/jobs/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Found ${result.new_jobs} new positions from ${result.sources_checked} sources!`);
        
        if (result.refreshed_at) {
          setLastRefresh(result.refreshed_at);
        }
        
        setPagination(prev => ({ ...prev, page: 1 }));
        
        const filtersRes = await fetch(`${API}/jobs/filters/options`);
        if (filtersRes.ok) {
          setFilterOptions(await filtersRes.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to refresh jobs");
      }
    } catch (error) {
      console.error("Error refreshing jobs:", error);
      toast.error("Failed to refresh jobs from sources");
    } finally {
      setRefreshing(false);
    }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const toggleBookmark = async (jobId) => {
    if (!user) {
      toast.error("Please login to bookmark jobs");
      return;
    }

    const isBookmarked = bookmarkedJobs.has(jobId);
    
    try {
      if (isBookmarked) {
        await fetch(`${API}/bookmarks/${jobId}`, {
          method: "DELETE",
          credentials: "include"
        });
        setBookmarkedJobs(prev => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
        toast.success("Bookmark removed");
      } else {
        await fetch(`${API}/bookmarks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ job_id: jobId })
        });
        setBookmarkedJobs(prev => new Set([...prev, jobId]));
        toast.success("Job bookmarked!");
      }
    } catch (error) {
      toast.error("Failed to update bookmark");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedFields([]);
    setSelectedCountries([]);
    setSelectedSources([]);
    setSelectedJobTypes([]);
    setSearchParams({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = searchQuery || selectedFields.length > 0 || selectedCountries.length > 0 || selectedSources.length > 0 || selectedJobTypes.length > 0;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Checkbox Filter Section Component
  const FilterSection = ({ title, sectionKey, options, selectedValues, onToggle, showCounts = false, maxVisible = 8 }) => {
    const [showAll, setShowAll] = useState(false);
    const isExpanded = expandedSections[sectionKey];
    const displayOptions = showAll ? options : options.slice(0, maxVisible);
    const hasMore = options.length > maxVisible;
    
    return (
      <div className="filter-section pb-4 border-b border-white/5">
        <button 
          onClick={() => toggleSection(sectionKey)}
          className="flex items-center justify-between w-full text-left mb-3 hover:text-[#D4AF37] transition-colors"
        >
          <h4 className="font-semibold text-white uppercase tracking-wide text-xs">
            {title}
            {selectedValues.length > 0 && (
              <span className="ml-2 text-[#D4AF37]">({selectedValues.length})</span>
            )}
          </h4>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
        
        {isExpanded && (
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
            {displayOptions.map(option => {
              const value = typeof option === 'object' ? option.value : option;
              const label = typeof option === 'object' ? option.label : option;
              const count = showCounts && filterOptions.source_counts?.[value];
              const isSelected = selectedValues.includes(value);
              
              return (
                <label
                  key={value}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                    isSelected ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'hover:bg-white/5'
                  }`}
                  data-testid={`filter-${sectionKey}-${value}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onToggle(value)}
                    className="border-gray-500 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                  />
                  <span className={`text-sm flex-1 ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                    {label}
                  </span>
                  {count && (
                    <span className="text-xs text-gray-500">{count}</span>
                  )}
                </label>
              );
            })}
            
            {hasMore && (
              <button 
                onClick={() => setShowAll(!showAll)}
                className="text-xs text-[#D4AF37] hover:underline mt-2 w-full text-left"
              >
                {showAll ? 'Show less' : `Show all ${options.length} options`}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const FilterSidebar = () => (
    <div className="space-y-4">
      {/* Position Type Filter */}
      <FilterSection
        title="Position Type"
        sectionKey="jobType"
        options={JOB_TYPES}
        selectedValues={selectedJobTypes}
        onToggle={(value) => toggleFilter(value, selectedJobTypes, setSelectedJobTypes)}
      />

      {/* Job Sources Filter - NEW */}
      <FilterSection
        title="Job Sources"
        sectionKey="source"
        options={filterOptions.sources}
        selectedValues={selectedSources}
        onToggle={(value) => toggleFilter(value, selectedSources, setSelectedSources)}
        showCounts={true}
        maxVisible={10}
      />

      {/* Research Field Filter */}
      <FilterSection
        title="Research Field"
        sectionKey="field"
        options={filterOptions.fields}
        selectedValues={selectedFields}
        onToggle={(value) => toggleFilter(value, selectedFields, setSelectedFields)}
        maxVisible={10}
      />

      {/* Country Filter */}
      <FilterSection
        title="Country"
        sectionKey="country"
        options={filterOptions.countries}
        selectedValues={selectedCountries}
        onToggle={(value) => toggleFilter(value, selectedCountries, setSelectedCountries)}
        maxVisible={10}
      />

      {/* Sort By */}
      <div className="filter-section pb-4">
        <h4 className="font-semibold text-white mb-3 uppercase tracking-wide text-xs">Sort By</h4>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="filter-sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="posted_date">Most Recent</SelectItem>
            <SelectItem value="deadline">Deadline</SelectItem>
            <SelectItem value="title">Title A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {hasActiveFilters && (
        <Button 
          variant="outline" 
          onClick={clearFilters}
          className="w-full border-white/20 text-white hover:bg-white/5 rounded-sm"
          data-testid="clear-filters-btn"
        >
          <X className="w-4 h-4 mr-2" />
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.05);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(212,175,55,0.3);
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(212,175,55,0.5);
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-white mb-2">
              Browse Jobs
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <p className="text-gray-400">
                {pagination.total} positions available
              </p>
              {lastRefresh && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Updated {formatRelativeTime(lastRefresh)}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <Link to="/post-job">
                <Button
                  className="bg-[#D4AF37] hover:bg-[#B8972E] text-black font-semibold px-4 py-2 rounded-sm flex items-center gap-2"
                  data-testid="post-job-btn"
                >
                  <Plus className="w-4 h-4" />
                  Post a Job
                </Button>
              </Link>
            )}
            <Button
              onClick={handleRefreshJobs}
              disabled={refreshing}
              className="bg-[#800020] hover:bg-[#600018] text-white font-semibold px-6 py-2 rounded-sm flex items-center gap-2"
              data-testid="refresh-jobs-btn"
            >
            {refreshing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Fetching Jobs...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Refresh
              </>
            )}
          </Button>
          </div>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Search positions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#D4AF37] rounded-sm w-full search-input"
              data-testid="jobs-search-input"
            />
          </div>
          <Button 
            type="submit"
            className="h-12 px-6 bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold uppercase tracking-wide text-xs rounded-sm btn-gold"
            data-testid="jobs-search-btn"
          >
            Search
          </Button>
          
          {/* Mobile Filter Button */}
          <Sheet>
            <SheetTrigger asChild>
              <Button 
                variant="outline" 
                className="h-12 px-4 border-white/20 text-white hover:bg-white/5 rounded-sm lg:hidden"
                data-testid="mobile-filter-btn"
              >
                <Filter className="w-5 h-5" />
                {hasActiveFilters && (
                  <span className="ml-1 bg-[#D4AF37] text-black text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {selectedFields.length + selectedCountries.length + selectedSources.length + selectedJobTypes.length}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="bg-[#0A0A0A] border-white/10 overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="text-white">Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterSidebar />
              </div>
            </SheetContent>
          </Sheet>
        </form>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-8 p-6 rounded-sm bg-[#0A0A0A] border border-white/10 max-h-[calc(100vh-6rem)] overflow-y-auto custom-scrollbar">
              <h3 className="font-semibold text-white mb-6 uppercase tracking-wide text-sm">Filters</h3>
              <FilterSidebar />
            </div>
          </aside>

          {/* Job Listings */}
          <main className="flex-1">
            {loading ? (
              <div className="space-y-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="p-6 rounded-sm bg-[#0A0A0A] border border-white/10">
                    <Skeleton className="h-4 w-24 mb-4" />
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-4" />
                    <Skeleton className="h-4 w-1/3" />
                  </div>
                ))}
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-400 text-lg mb-4">No positions found matching your criteria.</p>
                <Button 
                  variant="outline" 
                  onClick={clearFilters}
                  className="border-white/20 text-white hover:bg-white/5 rounded-sm"
                  data-testid="no-results-clear-btn"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {jobs.map((job, index) => (
                    <motion.div
                      key={job.job_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="job-card group relative overflow-hidden rounded-sm border border-white/10 bg-[#0A0A0A] p-6"
                      data-testid={`job-listing-${job.job_id}`}
                    >
                      {/* Position Type & Source Header */}
                      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/5">
                        <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase tracking-wider rounded ${
                          job.job_type === 'postdoc' ? 'bg-blue-500/20 text-blue-400' :
                          job.job_type === 'fellowship' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {job.job_type === 'postdoc' ? 'Postdoc' : job.job_type === 'fellowship' ? 'Fellowship' : 'Job'}
                        </span>
                        <span className="text-gray-500">|</span>
                        <span className="text-xs text-gray-400">
                          {job.source || 'Manual'}
                        </span>
                      </div>

                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="inline-block px-3 py-1 bg-[#800020]/20 text-[#D4AF37] text-xs font-semibold uppercase tracking-wide rounded-sm">
                              {job.field}
                            </span>
                          </div>
                          
                          <Link to={`/jobs/${job.job_id}`}>
                            <h3 className="font-serif text-xl font-semibold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                              {job.title}
                            </h3>
                          </Link>
                          
                          <p className="text-gray-400 mb-2 flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {job.institution}
                            {job.department && job.department !== "Research" && job.department !== "Department" && (
                              <span className="text-gray-500">- {job.department}</span>
                            )}
                          </p>
                          
                          {job.description && (
                            <p className="text-gray-500 text-sm mb-3 line-clamp-2">
                              {job.description.slice(0, 150)}...
                            </p>
                          )}
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}, {job.country}
                            </span>
                            {job.deadline && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                Deadline: {new Date(job.deadline).toLocaleDateString()}
                              </span>
                            )}
                            {job.source_url && (
                              <a 
                                href={job.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#D4AF37] hover:underline flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Apply Now
                                <RefreshCw className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                        
                        <button
                          onClick={() => toggleBookmark(job.job_id)}
                          className={`bookmark-btn p-2 ${bookmarkedJobs.has(job.job_id) ? 'text-[#D4AF37]' : 'text-gray-500 hover:text-[#D4AF37]'}`}
                          data-testid={`bookmark-btn-${job.job_id}`}
                        >
                          {bookmarkedJobs.has(job.job_id) ? (
                            <BookmarkCheck className="w-6 h-6" />
                          ) : (
                            <Bookmark className="w-6 h-6" />
                          )}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <Button
                      variant="outline"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      className="border-white/20 text-white hover:bg-white/5 rounded-sm disabled:opacity-50"
                      data-testid="pagination-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Previous
                    </Button>
                    <span className="text-gray-400">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      disabled={pagination.page === pagination.pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      className="border-white/20 text-white hover:bg-white/5 rounded-sm disabled:opacity-50"
                      data-testid="pagination-next-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
